import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './config';

/**
 * Firebase Storage管理サービスクラス
 */
export class StorageService {
  // 許可されるファイルタイプ
  private readonly PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/heic'];
  private readonly DOCUMENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

  // ファイルサイズ制限（バイト）
  private readonly MAX_PHOTO_SIZE = 5 * 1024 * 1024; // 5MB
  private readonly MAX_DOCUMENT_SIZE = 10 * 1024 * 1024; // 10MB

  /**
   * 写真アップロード
   */
  async uploadPhoto(file: File, dogId: string, userId: string): Promise<string> {
    try {
      // ファイルタイプ検証
      if (!this.PHOTO_TYPES.includes(file.type)) {
        throw new Error('対応していないファイル形式です。JPEG、PNG、HEICのみアップロード可能です。');
      }

      // ファイルサイズ検証
      if (file.size > this.MAX_PHOTO_SIZE) {
        throw new Error('ファイルサイズが大きすぎます。5MB以下のファイルを選択してください。');
      }

      // ファイル名を生成（タイムスタンプ + 元のファイル名）
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${timestamp}_${sanitizedFileName}`;

      // ストレージパス: photos/{userId}/{dogId}/{fileName}
      const storagePath = `photos/${userId}/${dogId}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      // アップロード
      await uploadBytes(storageRef, file);

      // ダウンロードURL取得
      const downloadURL = await getDownloadURL(storageRef);

      return downloadURL;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`写真のアップロードに失敗しました: ${error.message}`);
      }
      throw new Error('写真のアップロードに失敗しました');
    }
  }

  /**
   * 血統書アップロード
   */
  async uploadDocument(file: File, dogId: string, userId: string): Promise<string> {
    try {
      // ファイルタイプ検証
      if (!this.DOCUMENT_TYPES.includes(file.type)) {
        throw new Error('対応していないファイル形式です。PDF、JPEG、PNGのみアップロード可能です。');
      }

      // ファイルサイズ検証
      if (file.size > this.MAX_DOCUMENT_SIZE) {
        throw new Error('ファイルサイズが大きすぎます。10MB以下のファイルを選択してください。');
      }

      // ファイル名を生成
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${timestamp}_${sanitizedFileName}`;

      // ストレージパス: documents/{userId}/{dogId}/{fileName}
      const storagePath = `documents/${userId}/${dogId}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      // アップロード
      await uploadBytes(storageRef, file);

      // ダウンロードURL取得
      const downloadURL = await getDownloadURL(storageRef);

      return downloadURL;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`血統書のアップロードに失敗しました: ${error.message}`);
      }
      throw new Error('血統書のアップロードに失敗しました');
    }
  }

  /**
   * ファイル削除
   */
  async deleteFile(fileUrl: string): Promise<void> {
    try {
      // URLからストレージパスを抽出
      const storageRef = ref(storage, fileUrl);

      // 削除
      await deleteObject(storageRef);
    } catch (error) {
      // ファイルが存在しない場合はエラーを無視
      if (error instanceof Error && error.message.includes('object-not-found')) {
        console.warn('削除対象のファイルが見つかりませんでした:', fileUrl);
        return;
      }

      if (error instanceof Error) {
        throw new Error(`ファイルの削除に失敗しました: ${error.message}`);
      }
      throw new Error('ファイルの削除に失敗しました');
    }
  }

  /**
   * ダウンロードURL取得
   */
  async getDownloadUrl(storagePath: string): Promise<string> {
    try {
      const storageRef = ref(storage, storagePath);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`ダウンロードURLの取得に失敗しました: ${error.message}`);
      }
      throw new Error('ダウンロードURLの取得に失敗しました');
    }
  }

  /**
   * 複数写真の一括アップロード
   */
  async uploadMultiplePhotos(
    files: File[],
    dogId: string,
    userId: string
  ): Promise<string[]> {
    try {
      const uploadPromises = files.map((file) => this.uploadPhoto(file, dogId, userId));
      const downloadURLs = await Promise.all(uploadPromises);
      return downloadURLs;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`複数写真のアップロードに失敗しました: ${error.message}`);
      }
      throw new Error('複数写真のアップロードに失敗しました');
    }
  }

  /**
   * 複数ファイルの一括削除
   */
  async deleteMultipleFiles(fileUrls: string[]): Promise<void> {
    try {
      const deletePromises = fileUrls.map((url) => this.deleteFile(url));
      await Promise.all(deletePromises);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`複数ファイルの削除に失敗しました: ${error.message}`);
      }
      throw new Error('複数ファイルの削除に失敗しました');
    }
  }
}

// シングルトンインスタンスをエクスポート
export const storageService = new StorageService();
